// src/services/taskAssignmentApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export interface TaskAssignment {
  _id: string;
  title: string;
  description?: string;
  businessId: string;
  clientId: string;
  assignedUserId?: string;
  status: 'unassigned' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: Date;
  assignedAt?: Date;
  completedAt?: Date;
  assignmentMetrics?: {
    skillMatch: number;
    availabilityScore: number;
    proximityScore: number;
    workloadBalance: number;
    finalScore: number;
  };
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface ApprovedRejectedResponse {
  tasks: TaskAssignment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    approved: number;
    rejected: number;
    total: number;
  };
}

export interface AssignmentStats {
  pending: number;
  approved: number;
  rejected: number;
  approvedToday: number;
  rejectedToday: number;
  trends: {
    approvedTrend: number;
    rejectedTrend: number;
  };
}

export const taskAssignmentApi = createApi({
  reducerPath: 'taskAssignmentApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business-tasks`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['PendingTasks', 'TaskAssignments', 'AssignmentStats'],
  endpoints: (builder) => ({
    getPendingApprovalTasks: builder.query<TaskAssignment[], string>({
      query: (businessId) => `/pending-approval/${businessId}`,
      providesTags: ['PendingTasks']
    }),
    
    getApprovedRejectedTasks: builder.query<ApprovedRejectedResponse, {
      businessId: string;
      status?: 'approved' | 'rejected';
      page?: number;
      limit?: number;
      dateFrom?: string;
      dateTo?: string;
    }>({
      query: ({ businessId, status, page = 1, limit = 10, dateFrom, dateTo }) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (dateFrom) params.append('dateFrom', dateFrom);
        if (dateTo) params.append('dateTo', dateTo);
        
        return `/approved-rejected/${businessId}?${params.toString()}`;
      },
      providesTags: ['TaskAssignments']
    }),
    
    getAssignmentStats: builder.query<AssignmentStats, {
      businessId: string;
      period?: 'today' | 'week' | 'month';
    }>({
      query: ({ businessId, period = 'today' }) => 
        `/assignment-stats/${businessId}?period=${period}`,
      providesTags: ['AssignmentStats']
    }),
    
    // Debug endpoint (remove in production)
    debugTasks: builder.query<any, string>({
      query: (businessId) => `/debug/${businessId}`,
    }),
    
    triggerAutoAssign: builder.mutation<{ success: boolean; message: string }, string>({
      query: (taskId) => ({
        url: `/${taskId}/auto-assign`,
        method: 'POST'
      }),
      invalidatesTags: ['PendingTasks', 'AssignmentStats']
    }),
    
    approveTaskAssignment: builder.mutation<TaskAssignment, string>({
      query: (taskId) => ({
        url: `/${taskId}/approve`,
        method: 'PUT'
      }),
      invalidatesTags: ['PendingTasks', 'TaskAssignments', 'AssignmentStats']
    }),
    
    rejectTaskAssignment: builder.mutation<TaskAssignment, { taskId: string; reason: string }>({
      query: ({ taskId, reason }) => ({
        url: `/${taskId}/reject`,
        method: 'PUT',
        body: { reason }
      }),
      invalidatesTags: ['PendingTasks', 'TaskAssignments', 'AssignmentStats']
    })
  })
});

export const {
  useGetPendingApprovalTasksQuery,
  useGetApprovedRejectedTasksQuery,
  useGetAssignmentStatsQuery,
  useDebugTasksQuery,
  useTriggerAutoAssignMutation,
  useApproveTaskAssignmentMutation,
  useRejectTaskAssignmentMutation
} = taskAssignmentApi;